// Builds breadcrumb props for FAQ pages so each route renders the shared
// <Breadcrumbs> component (in "generic" mode) with a chain matching its level
// in the section. All helpers return `{ links, title }` ready to spread into
// `<Breadcrumbs locale={locale} links={links} title={title} />`.
//
// `homeLink` is the global home link entry (globalModules.links.breadcrumb.home).
// `page` is the route's parsed payload — it carries `hub: { title, urlSlug }`
// for category/question/subitem pages so the hub crumb is locale-aware.

const homeCrumb = homeLink => ({ slug: homeLink?.slug, value: homeLink?.title });

const hubCrumb = hub =>
  hub?.urlSlug && hub?.title ? { slug: hub.urlSlug, value: hub.title } : null;

const categoryCrumb = (hub, category) =>
  hub?.urlSlug && category?.slug
    ? {
        slug: `${hub.urlSlug}/${category.slug}`,
        value: category.title || category.shortLabel,
      }
    : null;

export const buildFaqHubBreadcrumbs = (page, homeLink) => ({
  links: [homeCrumb(homeLink)],
  title: page?.title,
});

export const buildFaqCategoryBreadcrumbs = (page, homeLink) => ({
  links: [homeCrumb(homeLink), hubCrumb(page?.hub)].filter(Boolean),
  title: page?.title,
});

export const buildFaqQuestionBreadcrumbs = (page, homeLink) => ({
  links: [
    homeCrumb(homeLink),
    hubCrumb(page?.hub),
    categoryCrumb(page?.hub, page?.category),
  ].filter(Boolean),
  title: page?.question,
});

export const buildFaqSubitemBreadcrumbs = (page, homeLink) => ({
  links: [homeCrumb(homeLink), hubCrumb(page?.hub), categoryCrumb(page?.hub, page?.parent)].filter(
    Boolean
  ),
  title: page?.title,
});

// FAQ UI label fallbacks per locale. Each map falls through to
// globalModules.translations.faq.<key> if the content team publishes one
// (Contentful keys: "FAQ - Go back", "FAQ - Choose topic", "FAQ - Close"),
// otherwise uses these so pilot locales work out of the box.
const GO_BACK_FALLBACKS = {
  en: 'Go back',
  'en-GB': 'Go back',
  'en-US': 'Go back',
  nl: 'Terug',
  'nl-NL': 'Terug',
  'nl-BE': 'Terug',
  fr: 'Retour',
  'fr-FR': 'Retour',
  'fr-BE': 'Retour',
  'de-DE': 'Zurück',
  'es-ES': 'Volver',
};

const CHOOSE_TOPIC_FALLBACKS = {
  en: 'Choose topic',
  'en-GB': 'Choose topic',
  'en-US': 'Choose topic',
  nl: 'Kies onderwerp',
  'nl-NL': 'Kies onderwerp',
  'nl-BE': 'Kies onderwerp',
  fr: 'Choisir un sujet',
  'fr-FR': 'Choisir un sujet',
  'fr-BE': 'Choisir un sujet',
  'de-DE': 'Thema wählen',
  'es-ES': 'Elegir tema',
};

const READ_MORE_FALLBACKS = {
  en: 'Read more',
  'en-GB': 'Read more',
  'en-US': 'Read more',
  nl: 'Lees meer',
  'nl-NL': 'Lees meer',
  'nl-BE': 'Lees meer',
  fr: 'En savoir plus',
  'fr-FR': 'En savoir plus',
  'fr-BE': 'En savoir plus',
  'de-DE': 'Mehr lesen',
  'es-ES': 'Leer más',
};

const CLOSE_FALLBACKS = {
  en: 'Close',
  'en-GB': 'Close',
  'en-US': 'Close',
  nl: 'Sluiten',
  'nl-NL': 'Sluiten',
  'nl-BE': 'Sluiten',
  fr: 'Fermer',
  'fr-FR': 'Fermer',
  'fr-BE': 'Fermer',
  'de-DE': 'Schließen',
  'es-ES': 'Cerrar',
};

export const getGoBackLabel = (globalModules, locale) =>
  globalModules?.translations?.faq?.goBack || GO_BACK_FALLBACKS[locale] || 'Go back';

export const getChooseTopicLabel = (globalModules, locale) =>
  globalModules?.translations?.faq?.chooseTopic || CHOOSE_TOPIC_FALLBACKS[locale] || 'Choose topic';

export const getCloseLabel = (globalModules, locale) =>
  globalModules?.translations?.faq?.close || CLOSE_FALLBACKS[locale] || 'Close';

// Also used by the FAQ accordion section, which renders via renderSections
// and has no globalModules — it passes null to use the per-locale fallbacks
// (Contentful key: "FAQ - Read more").
export const getReadMoreLabel = (globalModules, locale) =>
  globalModules?.translations?.faq?.readMore || READ_MORE_FALLBACKS[locale] || 'Read more';

// Back-link helpers for the FAQ sidebar. Hub pages return null (no parent).
// Each returns `{ href, label }` consumed by <FaqSidebar backLink={...} />.

export const buildFaqCategoryBackLink = (page, label) =>
  page?.hub?.urlSlug ? { href: `/${page.hub.urlSlug}`, label } : null;

export const buildFaqQuestionBackLink = (page, label) =>
  page?.hub?.urlSlug && page?.category?.slug
    ? { href: `/${page.hub.urlSlug}/${page.category.slug}`, label }
    : null;

export const buildFaqSubitemBackLink = (page, label) =>
  page?.hub?.urlSlug && page?.parent?.slug
    ? { href: `/${page.hub.urlSlug}/${page.parent.slug}`, label }
    : null;
